/**
 * Connor's turn, with the steps arriving as they happen.
 *
 * Josh, 2026-09-07: he wanted to see the route an answer took, live — "just
 * enough to tell what you went through to find the answer without giving a
 * thesis".
 *
 * ⚠️ IT FALLS BACK TO THE PLAIN CALL ON ANY FAILURE, AND THAT IS THE WHOLE
 * SAFETY OF SHIPPING IT. Streaming has more ways to go wrong than a POST does:
 * a proxy that buffers, a browser without ReadableStream, a connection that
 * drops mid-answer. None of those should cost the member their answer, so a
 * broken stream costs only the progress line.
 *
 * ⚠️ AND THE FALLBACK RUNS THE TURN AGAIN. It is a read — no send, no save —
 * so running it twice is safe, which is exactly why Connor's tools were kept
 * read-only. If that ever stops being true this fallback has to go.
 */
import { api } from './api';

export interface ConnorTurnResult {
  reply: string;
  actions: (
    | { type: 'openPage'; page: string; id?: string }
    | { type: 'askDesk'; message: string }
  )[];
  toolsUsed: string[];
  truncated: boolean;
  /** A lookup ran and came back empty. See ConnorReply in the API. */
  unresolved?: boolean;
}

const BASE = import.meta.env.VITE_API_BASE_URL || '';

/**
 * Run one turn, calling `onStep` as each step begins.
 *
 * `onStep` is best-effort: it may be called several times, or never, and the
 * answer is the same either way.
 */
export async function connorTurn(
  messages: { role: 'user' | 'assistant'; content: string }[],
  token: string | null,
  onStep: (label: string) => void,
): Promise<ConnorTurnResult> {
  try {
    const res = await fetch(`${BASE}/api/connor/chat/stream`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ messages }),
    });
    if (!res.ok || !res.body) throw new Error('no stream');

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let done: ConnorTurnResult | null = null;
    let failed = false;

    for (;;) {
      const chunk = await reader.read();
      if (chunk.done) break;
      buffer += decoder.decode(chunk.value, { stream: true });

      /*
        SSE frames are separated by a blank line. Anything after the last one is
        a partial frame and stays in the buffer — parsing it now would throw on
        half a JSON object roughly every other chunk.
      */
      const frames = buffer.split('\n\n');
      buffer = frames.pop() ?? '';

      for (const frame of frames) {
        const event = /^event:\s*(.+)$/m.exec(frame)?.[1]?.trim();
        const raw = /^data:\s*(.+)$/m.exec(frame)?.[1];
        if (!event || !raw) continue;
        try {
          const data = JSON.parse(raw) as Record<string, unknown>;
          if (event === 'step' && typeof data.label === 'string') onStep(data.label);
          else if (event === 'done') done = data as unknown as ConnorTurnResult;
          else if (event === 'failed') failed = true;
        } catch {
          // A frame we cannot read is a frame we skip. The 'done' one matters.
        }
      }
    }

    if (failed) throw new Error('turn failed');
    if (!done) throw new Error('stream ended with no answer');
    return done;
  } catch {
    // See the header: the turn is a read, so running it again is safe.
    return (await api.connorChat(messages)) as ConnorTurnResult;
  }
}
